import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, ElementRef, Inject, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, ValidatorFn } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { WorkspaceUser } from "@app/_classes/timeghost";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { useAbsencesForUser } from "@app/_helpers/absence/utils";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { pushError } from "@app/_helpers/globalErrorHandler";
import {
	ErrorResponse,
	isBreakRuleError,
	isWorkingHoursError,
	parseErrorResponse,
} from "@app/_helpers/is-error-object";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { hasPermissionByKey, isAdmin, isSubordinate, isSupervisor } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import { createRxValueWithCache } from "@app/_helpers/rx-value";
import { getComegoFutureLockedDate, getComegoLockedDate } from "@app/_helpers/timeLock";
import {
	coerceTimeFormat,
	createRxValue,
	DEFAULT_PERMISSION_GROUPS,
	distinctUntilChangedJson,
	fromRxValue,
	getActiveSchedule,
	hasPermission,
	nextTick,
	parseTimeFormat,
	stringify,
	timeFormatToDuration,
} from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { ComegoSuggestionService } from "@app/services/come-and-go-suggestion.service";
import { PROCEED_WITH_NORMAL_STOP } from "@app/services/comego-recording-rulebreak.service";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { TimeDatePickerComponent } from "@app/shared/time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "@app/shared/time-date-picker/time-date-picker-config";
import { Order } from "@datorama/akita";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	ComegoTimeType,
	Logger,
	UserSettings,
	UserSettingsQuery,
	WorkingHourSettings as WHSettings,
	Workspace,
} from "@timeghost/web-api";
import {
	addDays,
	addMilliseconds,
	addMinutes,
	addSeconds,
	areIntervalsOverlapping,
	endOfDay,
	endOfISOWeek as endOfWeek,
	format,
	isSameDay,
	isSameMinute,
	isValid,
	startOfDay,
	startOfISOWeek as startOfWeek,
} from "date-fns/esm";
import { T } from "lodash/fp";
import { clamp, omit, pick, xorBy } from "lodash-es";
import {
	combineLatest,
	distinctUntilChanged,
	filter,
	firstValueFrom,
	map,
	of,
	startWith,
	switchMap,
	take,
	takeWhile,
	tap,
} from "rxjs";
import { firstBy } from "thenby";
import {
	ComeGoTimeControl,
	checkTimeOrder,
	generateTimeTableFromSchedule,
	maxLengthDescription,
} from "../come-and-go-create-dialog/come-and-go-create-dialog.component";
import { COMEGO_ICON_MAP, COMEGO_MAX_DESCRIPTION } from "../come-and-go-create-dialog/come-and-go-utils";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "../user-single-picker-dialog/user-single-picker-dialog.component";

type TimesSingleControl = ComeGoTimeControl & { recording: FormControl<boolean>; id: FormControl<string> };
type TimesControl = FormGroup<TimesSingleControl>;
const log = new Logger("ComeAndGoMultiDialogComponent");
/** Shown as a non-blocking notice (not via `error$`) so submit is not gated by force rules. */
export const WARNING_ERROR_MESSAGES = [
	"errors.times.comego.scheduleTargetExceeded-alert",
	"errors.times.comego.dailyMaxHours-created",
];
export const VerifyBlockComegoPast: (currentuser: () => UserSettings, choosenUser: () => string) => ValidatorFn = (
	_currentuser,
	_choosenUser
) => {
	return (form: FormControl<Date>) => {
		const blockComego = _currentuser().workspace.settings.blockComego;
		if (!blockComego) return null;
		const date = form.getRawValue();
		const currentuser = _currentuser();
		const choosenUser = _choosenUser();
		const isAdmin = !!currentuser.workspace.users.find((x) => x.admin && x.id === currentuser.id);
		const minDate = getComegoLockedDate(currentuser, choosenUser);
		const maxDate = getComegoFutureLockedDate(currentuser, choosenUser);
		if (minDate && maxDate) {
			if (date < minDate || date > maxDate) return { blockEdit: true };
			else return null;
		} else if (minDate) {
			if (date < minDate) return { blockEdit: true };
		} else if (maxDate) {
			if (date > maxDate) return { blockEdit: true };
		} else return null;
	};
};
/** Close result: persist the original (violating) projection instead of the suggested fix. */
export const PROCEED_WITH_VIOLATION = Symbol("PROCEED_WITH_VIOLATION");

export interface WorkingHoursUpdateData {
	time?: {
		value: ComegoTime[];
		correctTimeRanges?: boolean;
		lockItem?: boolean;
		refetchFromDate?: boolean;
		date: Date;
	};
	error?: ErrorResponse;
	hiddenFields?: string[];
	disabledFields?: string[];
	allowedTimeTypes?: ComegoTimeType[];
	enableNextDayNavigation?: boolean;
	forceRecreateDay?: boolean;
	disabled?: boolean;
	/**
	 * When true the dialog received draft entries that have not yet been persisted.
	 * The open recording is still running in the backend. Submit must delete all persisted
	 * entries for the day and create all form rows. Discard (when allowed) stops at now
	 * without applying the suggested split; cancel closes without stopping.
	 */
	pendingRecordingFix?: boolean;
	/**
	 * When true the dialog shows a suggested fix for a projected day sync.
	 * "Save with violation" closes with PROCEED_WITH_VIOLATION so the caller can
	 * persist the original (violating) projection; cancel aborts the sync.
	 */
	pendingProjectionFix?: boolean;
	/** Persisted open work recording; used when discarding the suggested correction. */
	openRecording?: ComegoTime;
	/**
	 * Extra persisted ids to delete on submit (e.g. day entries omitted from `time.value`
	 * because a projected sync removed them). Merged into forceRecreateDay / pendingRecordingFix deletes.
	 */
	idsToDelete?: string[];
	/**
	 * Informational rule-violation messages shown under the info banner.
	 * Does not gate submit (unlike `error`).
	 */
	violationNotices?: Array<{
		message: string;
		args?: Record<string, unknown>;
		start?: string;
		end?: string;
	}>;
}

/** Returns true for client-side draft ids that were never persisted to the backend. */
const isDraftId = (id?: string | null): boolean => id?.startsWith("draft-") ?? false;

const resolveEntryTime = (value: string | Date | null | undefined, formDate: Date): Date | null => {
	if (value == null || value === "") return null;
	if (value instanceof Date) return value;
	if (typeof value === "string" && value.includes("T")) {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}
	return coerceTimeFormat(value, formDate);
};
export type ComeGoTime = Omit<ComegoTime, "workspace" | "id"> & { recording?: boolean; typeId?: string };

export type absenceSubcategories = {
	id: string;
	enabled: boolean;
	title: string;
	description?: string;
	durationType?: "basedOnTargetHours" | "customValue";
	duration?: string | null;
	isStatic?: boolean;
	visibleUsers: any[];
	visibleForGroups: any[];
};
type WorkingHourSettings = WHSettings & {
	minimumBreakDurationForCredit: number;
};
@UntilDestroy()
@ObserveFormGroupErrors()
@Component({
	selector: "tg-come-and-go-day-update-dialog",
	templateUrl: "./come-and-go-rulebreak-dialog.component.html",
	styleUrls: ["./come-and-go-rulebreak-dialog.component.scss"],
})
export class ComeAndGoRulebreakFixDialogComponent implements OnInit, AfterViewInit {
	private readonly clock = inject(ClockService);
	private readonly hostEl = inject(ElementRef<HTMLElement>);
	readonly iconMap = COMEGO_ICON_MAP;
	readonly isAMPM$ = this.userSettingsQuery.select().pipe(map((x) => !x.settings?.timeFormat24h));
	readonly now$ = this.clock.now$;
	readonly error$ = createRxValue<ErrorResponse>();
	/** Informational only: schedule target exceeded; cleared after first form change. */
	readonly scheduleTargetNotice$ = createRxValue<ErrorResponse | null>(null);
	absenceSubcategories: {
		id: string;
		enabled: boolean;
		title: string;
		description?: string;
		durationType?: "basedOnTargetHours" | "customValue";
		duration?: string | null;
		isStatic?: boolean;
		visibleUsers: any[];
		visibleForGroups: any[];
	}[] = [];
	constructor(
		private ref: MatDialogRef<ComeAndGoRulebreakFixDialogComponent>,
		private dialog: MatDialog,
		private userSettingsQuery: UserSettingsQuery,
		private app: AppService,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private recordService: RecordToolbarService,
		private comegoSuggestion: ComegoSuggestionService,
		private deletionService: DeletionUndoService,
		@Inject(MAT_DIALOG_DATA)
		private data: WorkingHoursUpdateData
	) {
		if (!this.data) this.data = {};
	}

	readonly isLoading = createRxValue(false);
	readonly isDeletingEntries$ = createRxValue(false);
	readonly maxLength = COMEGO_MAX_DESCRIPTION;
	@ViewChild("confirmDeleteDialog", { static: true }) confirmDeleteDialog: TemplateRef<any>;
	private originalDate: Date | null = null;
	private activeSchedule: ReturnType<typeof getActiveSchedule> = null;
	get enableNextDayNavigation() {
		return !!this.data.enableNextDayNavigation;
	}
	readonly group = new FormGroup({
		user: new FormControl<Workspace["users"][0]>(null),
		times: new FormArray<TimesControl>([], {
			validators: [
				checkTimeOrder(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					},
					() => {
						const date = this.group?.getRawValue()?.date as Date;
						return date;
					},
					() => {
						const date = this.group?.getRawValue()?.date as Date;
						if (!date || !isValid(date)) return null;
						return {
							absence: 0,
							pause: 0,
							work: 0,
						} as { [K in ComegoTimeType]: number };
					},
					// last working day check
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const currentUser = this.group?.getRawValue()?.user as WorkspaceUser;
						const user = {
							...this.userSettingsQuery.getValue(),
							...((currentUser?.id && { id: currentUser?.id }) || {}),
						};
						const pastDay = addDays(date.getTime(), -1);
						const lastWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), pastDay) && x.user?.id === user.id,
							sortBy(a, b) {
								const aend = new Date(a.end || new Date()),
									bend = new Date(b.end || new Date());
								return bend.getTime() - aend.getTime();
							},
						})?.[0];
						if (!lastWorkingDayTime) return null;
						return {
							...lastWorkingDayTime,
							start: new Date(lastWorkingDayTime.start),
							end: new Date(lastWorkingDayTime.end || new Date()),
						};
					},
					// next working day check
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const user = this.userSettingsQuery.getValue();
						const dayOfTime = addDays(date.getTime(), 1);
						const nextWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), dayOfTime) && x.user?.id === user.id,
							sortBy(a, b) {
								const atime = new Date(a.start || new Date()),
									btime = new Date(b.start || new Date());
								return atime.getTime() - btime.getTime();
							},
							sortByOrder: Order.ASC,
						})?.[0];
						if (!nextWorkingDayTime) return null;
						return {
							...nextWorkingDayTime,
							start: new Date(nextWorkingDayTime.start),
							end: new Date(nextWorkingDayTime.end || new Date()),
						};
					},

					{ ignoreRulesForRecordingTimes: false }
				),
				maxLengthDescription(),
			],
			updateOn: "blur",
		}),
		date: new FormControl<Date>(new Date(), {
			validators: [
				VerifyBlockComegoPast(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					}
				),
			],
		}),
	});

	readonly absenceSubcategories$value = createRxValue(
		this.userSettingsQuery.getValue().workspace?.["workinghours"]?.[
			"absenceSubcategories"
		] as absenceSubcategories[]
	);
	readonly isSubAbsence = combineLatest([
		this.absenceSubcategories$value.asObservable(),
		this.group.valueChanges.pipe(startWith(this.group.value)),
	]).pipe(
		map(([absences, formGroup]) => {
			formGroup = this.group.value;
			let isAbsences: boolean[] = [];
			formGroup.times.forEach((comegoGroup) => {
				isAbsences.push(!!absences.find((absence) => absence.id == comegoGroup.type));
			});
			return isAbsences;
		})
	);

	/** Set when we delete from this dialog so we can refresh existing entries on undo. */
	private lastDeletedViewId: string | null = null;
	readonly existingEntries$ = createRxValue<ComegoTime[]>([]);
	readonly duration$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
		this.now$,
	]).pipe(
		map(([_, existingEntries, now]) => {
			const x = this.group.getRawValue();
			const user = this.userSettingsQuery.getValue();
			const wh = user.workspace.schedules.workspace["workinghours"] as any;
			const shortBreakDuration = (wh?.minimumBreakDurationForCredit ?? 0.25) * 3600;

			// Start with existing entries duration (exclude rows that recreate/submit will replace)
			const retainedExisting = this.getRetainedExistingEntries(existingEntries);
			const baseSum = retainedExisting.reduce(
				(acc, r) => {
					if (!r.start || !r.end) return acc;
					const start = new Date(r.start).getTime(),
						end = new Date(r.end).getTime();
					if (end < start) return acc;
					const sum = (end - start) / 1000;
					if (r.type === "work") acc.working += sum;
					else if (r.type === "pause") {
						acc.pause += sum;
						if (sum > shortBreakDuration) acc.realBreak += sum;
					} else if (r.type === "absence") acc.absence += sum;
					return acc;
				},
				{ working: 0, pause: 0, realBreak: 0, absence: 0 }
			);

			// Add form entries duration
			return x.times.reduce((acc, r) => {
				if (!r.start || !r.end) return acc;
				const start = coerceTimeFormat(r.start!).getTime(),
					end = r.recording ? now.getTime() : coerceTimeFormat(r.end!).getTime();
				if (end < start) return acc;
				const sum = (end - start) / 1000;
				if (r.type === "work") acc.working += sum;
				else if (r.type === "pause") {
					acc.pause += sum;
					if (sum > shortBreakDuration) acc.realBreak += sum;
				} else if (r.type === "absence") acc.absence += sum;
				else acc.absence += sum;
				return acc;
			}, baseSum);
		})
	);
	readonly overlappingExistingEntryIds$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
	]).pipe(
		map(([_, existingEntries]) => {
			const x = this.group.getRawValue();
			const times = x.times;
			const date = x.date;
			const overlappingIds = new Set<string>();

			const retainedExisting = this.getRetainedExistingEntries(existingEntries);
			if (!retainedExisting.length || !date) return [];

			for (const formEntry of times) {
				if (formEntry.type !== "work" && formEntry.type !== "pause") continue;
				if (!formEntry.start || !formEntry.end || formEntry.recording) continue;

				const formStart = coerceTimeFormat(formEntry.start, date);
				const formEnd = coerceTimeFormat(formEntry.end, date);
				if (!isValid(formStart) || !isValid(formEnd) || formStart >= formEnd) continue;

				for (const existing of retainedExisting) {
					if (existing.type !== "work" && existing.type !== "pause") continue;
					if (!existing.start || !existing.end || !existing.id) continue;

					const existingStart = new Date(existing.start);
					const existingEnd = new Date(existing.end);
					if (!isValid(existingStart) || !isValid(existingEnd) || existingStart >= existingEnd) continue;

					if (
						areIntervalsOverlapping(
							{ start: formStart, end: formEnd },
							{ start: existingStart, end: existingEnd }
						) &&
						!(
							formEnd.getTime() === existingStart.getTime() ||
							existingEnd.getTime() === formStart.getTime()
						)
					) {
						overlappingIds.add(existing.id);
					}
				}
			}

			return Array.from(overlappingIds);
		})
	);
	readonly workspace$isAdmin = fromRxValue(
		this.userSettingsQuery.select((x) => hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, x))
	);
	readonly workspace$canManageOthers = fromRxValue(
		this.userSettingsQuery.select((x) => isAdmin(x) || isSupervisor(x))
	);
	readonly canUpdateTimes$ = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes")))
	);
	readonly preventDeviatingWorkingHours = this.userSettingsQuery
		.select()
		.pipe(
			map(
				(userSettings) =>
					userSettings.workspace?.schedules?.workspace?.workinghours?.preventDeviatingWorkingHours
			)
		);
	private readonly rulesEnforced$ = this.preventDeviatingWorkingHours.pipe(
		startWith(
			!!this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours
				?.preventDeviatingWorkingHours
		)
	);
	/** True when existing + form entries combined would meet or exceed the daily max work duration (disables add button). Subscribes to form/entries/wh only when rules are enforced. */
	readonly isDailyMaxReachedOrExceededByCombined$ = this.rulesEnforced$.pipe(
		switchMap((enforced) => {
			if (!enforced) return of(false);
			return combineLatest([
				this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
				this.existingEntries$.value$,
				this.userSettingsQuery.select(
					(u) =>
						(u.workspace?.schedules as { workspace?: { workinghours?: { dailyMaxHours?: number } } })
							?.workspace?.workinghours
				),
			]).pipe(
				map(([_, existingEntries, wh]) => {
					const raw = this.group.getRawValue();
					const date = raw?.date as Date | null;
					if (!date || !isValid(date)) return false;
					const formEntries = (raw?.times ?? []).map(
						(t: { start?: string; end?: string; type: ComegoTimeType }) => ({
							start: t.start ?? "",
							end: t.end ?? "",
							type: t.type,
						})
					);
					return this.comegoSuggestion.isDailyMaxReachedOrExceededByCombined(
						date,
						this.getRetainedExistingEntries(existingEntries),
						formEntries,
						wh ?? undefined
					);
				})
			);
		})
	);
	getErrorObservable(controlName: string) {
		const usersettings = this.userSettingsQuery.getValue();
		const aliasMap = {
			name: "time.name",
			start: "timer.time.start",
			end: "timer.time.end",
		};
		const aliasName = aliasMap[controlName] || controlName;
		return useFormErrorObservable(this, { respectState: true })(
			controlName,
			() => this.group.controls[controlName],
			{
				required: (error, ctrl) => {
					if (controlName === "task") return { content: "errors.record.desc-required", args: {} };
					if (controlName === "project") return { content: "errors.record.project-req", args: {} };
					if (controlName === "times") return { content: "errors.times.comego.required", args: {} };
					return {
						content: "errors.required",
						args: { field: aliasName },
					};
				},
				same: (error, ctrl) => {
					const aliasFields = Object.entries(ctrl.errors?.fields || {}).reduce(
						(acc, [key, value]) => ({ ...acc, [key]: aliasMap[value as string] }),
						{}
					);
					return { content: "errors.sameValue", args: { ...aliasFields } };
				},
				breakRule: (error) => {
					if (error.type === "break")
						return {
							content: "errors.times.comego.breakRuleNotice",
							args: pick(error, "workingHours", "minutesDue"),
						};
					if (error.type === "rest")
						return {
							content: "errors.times.comego.breakBetweenWorkingDays",
							args: { breakBetweenWorkingDays: error.value },
						};
					if (error.type === "maxHours")
						return { content: "errors.times.comego.dailyMaxHours", args: { dailyMaxHours: error.value } };
					return null;
				},
				minlength: (error, ctrl) => ({
					content: "errors.minlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlength: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlengthDescription: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: "Description", length: COMEGO_MAX_DESCRIPTION },
				}),
				sameType: (error, ctrl) => {
					return { content: "Subsequent entries must not be the same type", args: {} };
				},
				missingType: (error, ctrl) => {
					return { content: "Entries are missing a stop entry", args: {} };
				},
				invalidType: (error, ctrl) => {
					return { content: 'Before last type must not be of type "pause"', args: {} };
				},
				intervening: (error, ctrl) => {
					return { content: "errors.times.comego.intervening", args: {} };
				},
				breakToShort: (error, ctrl) => {
					const settings = usersettings.workspace.schedules.workspace["workinghours"] as any;
					return {
						content: "errors.times.comego.shortBreak",
						args: { length: (settings?.minimumBreakDurationForCredit ?? 0.25) * 60 },
						warning: true,
					};
				},
				blockEdit: (error, ctrl) => {
					return { content: "errors.times.comego.blockEdit", args: {} };
				},
				blockEditFuture: (error, ctrl) => {
					return { content: "errors.times.comego.blockEditFuture", args: {} };
				},
				blockComegoPast: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoPast",
						args: { value: blockComego.period, range: blockComego.range },
					};
				},
				blockComegoFuture: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoFuture",
						args: { value: blockComego.futurePeriod, range: blockComego.futureRange },
					};
				},
			},
			(key) => key,
			{
				initialValidate: false,
			}
		);
	}
	readonly postDiffUser$ = combineLatest([
		this.group.valueChanges.pipe(
			map((x) => x?.user?.id),
			distinctUntilChanged()
		),
		this.userSettingsQuery.select(),
	]).pipe(map(([uid, user]) => uid && uid !== user.id));

	readonly currentDateHasSchedule$ = combineLatest([this.group.valueChanges, this.userSettingsQuery.select()]).pipe(
		map(([value, user]) => {
			const { date } = this.group.getRawValue() ?? {};
			if (!date) return null;
			const sched = getActiveSchedule({ ...user, id: value.user?.id || user.id }, date);
			return sched?.enabled && !!sched.items.find((x) => x.day === startOfDay(date).getUTCDay() && x.enabled);
		})
	);
	readonly hiddenFields = createRxValue<{ [key: string]: boolean }>({});
	readonly canAddTime = createRxValue(true);
	readonly enabledTypes = createRxValue<{ [K in ComegoTimeType]?: boolean }>({
		absence: true,
		pause: true,
		work: true,
	});
	readonly comegoDescriptionToggle$ = createRxValue<boolean>(false);
	readonly userHasChangedDescription = createRxValue<boolean>(false);
	private newWorkEntryDuration: number | null = null;
	readonly wasRecordingItems = createRxValue<ComegoTime[]>([]);
	private preExistingEntriesById = new Map<string, ComegoTime>();
	private initialBoundsById = new Map<string, { start: string; end: string; type: ComegoTimeType }>();
	soleEditableTimeField: { rowIndex: number; field: "start" | "end" } | null = null;
	private hasSelectedSoleEditableField = false;
	private soleEditableFocusTimer: ReturnType<typeof setTimeout> | null = null;
	readonly showContactAdmin = createRxValue(false);
	async ngOnInit() {
		this.ref._containerInstance._config.autoFocus = false;
		this.ref.updateSize("700px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);
		const now = this.clock.now();
		const userSettings = this.userSettingsQuery.getValue();
		const userFromTime = this.data.time?.value?.find((d) => d.user)?.user ?? { id: userSettings.id };
		const user = userSettings.workspace.users?.find((d) => d.id === userFromTime.id) as WorkspaceUser;
		this.wasRecordingItems.update([...(this.data.time?.value ?? [])]);
		if (this.data.time.refetchFromDate && user) {
			const start = startOfDay(this.data.time.date),
				end = endOfDay(start);
			this.isLoading.next(true);
			const serviceManagedSnapshot = [...this.wasRecordingItems.value];
			const refetched = await this.comegoService.adapter
				.post<any>("/get/comego", {
					$filter: `start le '${end.toISOString()}' and start ge '${start.toISOString()}' and user__id eq '${user.id}'`,
				})
				.toPromise()
				.finally(() => {
					this.isLoading.next(false);
				});
			const refetchedIds = new Set((refetched ?? []).map((e: ComegoTime) => e.id).filter(Boolean));

			if (this.data.pendingRecordingFix) {
				// The draft entries in wasRecordingItems contain the proposed split (stopped
				// recording + pause + optional resume work). The backend still has the original
				// open recording. We exclude any refetched entry whose real server id matches a
				// service-managed (non-draft) id, then merge in all drafts.
				const realServiceIds = new Set(
					serviceManagedSnapshot.map((e) => e.id).filter((id): id is string => !!id && !isDraftId(id))
				);
				const filteredRefetched = (refetched ?? []).filter(
					(e: ComegoTime) => !e.id || !realServiceIds.has(e.id)
				);
				this.data.time.value = [...filteredRefetched, ...serviceManagedSnapshot].sort(
					(a: ComegoTime, b: ComegoTime) => new Date(a.start).getTime() - new Date(b.start).getTime()
				);
			} else {
				const crossDayEntries = serviceManagedSnapshot.filter((e) => e.id && !refetchedIds.has(e.id));
				this.data.time.value = [...(refetched ?? []), ...crossDayEntries].sort(
					(a: ComegoTime, b: ComegoTime) => new Date(a.start).getTime() - new Date(b.start).getTime()
				);
			}
		}
		this.userSettingsQuery
			.select()
			.pipe(untilDestroyed(this))
			.subscribe((user) => {
				this.canAddTime.next(hasPermissionByKey(user, "groupsCanComegoCreateTimes"));
				nextTick(() => this.applyLimitedUpdatePermissions());
			});
		this.group.patchValue({
			user,
			date: this.data.time?.date ?? now,
		});
		if (!hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, userSettings)) {
			this.group.controls.user.disable({ emitEvent: false });
			this.group.controls.date.disable({ emitEvent: false });
		}
		this.originalDate = new Date(this.data.time?.date ?? now);
		this.activeSchedule = getActiveSchedule({ ...userSettings, id: user.id }, this.group.getRawValue().date);
		this.loadExistingEntriesForRecreateFlow();

		if (this.data?.hiddenFields?.length)
			this.hiddenFields.next(this.data.hiddenFields.reduce((acc, r) => ({ ...acc, [r]: true }), {}));
		this.group.updateValueAndValidity();
		let error: any = this.data.error;
		if (isBreakRuleError(error)) {
			if (!this.data.pendingRecordingFix) {
				pushError(error.message, error.args);
			}
			error = null;
		}
		if (WARNING_ERROR_MESSAGES.includes(error?.message)) {
			this.scheduleTargetNotice$.next(error);
			error = null;
		}

		this.newWorkEntryDuration = 30 * 60 * 1000; // default 30 min until API returns
		const userId = this.group.getRawValue()?.user?.id ?? this.userSettingsQuery.getValue().id;
		this.comegoSuggestion.getLastWorkEntryDurationMs(userId).then((duration) => {
			if (duration != null && duration > 0) this.newWorkEntryDuration = duration;
		});

		// auto generate time ranges based on time values
		nextTick(() => {
			this.data.time.value?.forEach((t) => {
				const start = new Date(t.start);
				if (t["typeId"]) {
					t.type = t["typeId"];
				}
				const isRecording = !t.end;
				const end = t.end ? new Date(t.end) : isSameDay(start, now) ? addMinutes(now, 1) : endOfDay(start);
				this.addTimeRange(t.type, start, end, false, isRecording, t.name, t.id);
			});
			this.group.controls.times.updateValueAndValidity();
			this.sortTimes(true);
			this.buildEntryProvenanceMaps();
			this.applyLimitedUpdatePermissions();
		});

		// validate and check errors on next tick, updates ui instantly
		if (error) {
			nextTick(() => {
				this.error$.next(error);
				if (!this.data.disabled && isWorkingHoursError(error))
					this.checkValueOnceChange(this.group.getRawValue().times);
				if (this.data.disabled) {
					this.group.markAsPristine();
					this.group.disable();
				}
			});
		}
		if (this.data.enableNextDayNavigation) {
			this.group.controls.date.disable();
		}
		if (!this.data.disabled && this.data.disabledFields?.length)
			nextTick(() => {
				this.data.disabledFields.forEach((x) => {
					if (this.group.controls[x]?.enabled) {
						this.group.controls[x].disable();
					} else {
						if (x === "time.type")
							this.group.controls.times.controls.forEach((t) => t.controls.type.disable());
						if (x === "time") {
							this.group.controls.times.controls.forEach((t) => t.disable());
							this.group.controls.times.disable();
						}
						if (x === "time.add") this.canAddTime.next(false);
					}
				});
			});
		if (this.data.allowedTimeTypes?.length) {
			this.enabledTypes.next(this.data.allowedTimeTypes.reduce((acc, r) => ({ ...acc, [r]: true }), {}));
		}
		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => x?.date),
				distinctUntilChangedJson()
			)
			.subscribe(async () => {
				const value = this.group.getRawValue();
				const u = value?.user;
				const date = value?.date;
				if (u && date)
					this.activeSchedule = getActiveSchedule({ ...this.userSettingsQuery.getValue(), id: u.id }, date);
				this.group.controls.times.updateValueAndValidity();
			});

		// Listen for end time changes to convert "00:00" to "23:59"
		this.group.controls.times.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
			this.group.controls.times.controls.forEach((timeControl, index) => {
				const endControl = timeControl.get("end");
				if (endControl && endControl.value === "00:00") {
					endControl.patchValue("23:59", { emitEvent: false });
				}
			});
		});

		// this.group.valueChanges.pipe(take(1)).subscribe(() => {
		// 	this.sortTimes(true);
		// 	this.group.setMustChange(this.group.getRawValue());
		// });
		this.absenceSubcategories = userSettings?.workspace?.["workinghours"]?.["absenceSubcategories"] || [];
	}
	ngAfterViewInit(): void {
		this.group.updateValueAndValidity();
	}
	// private sortingState = false;
	blurFormSort(ev: Event, currentIndex?: number, field?: "start" | "end") {
		if (this.scheduleTargetNotice$.value) this.scheduleTargetNotice$.next(null);
		const canSyncAdjacent = !this.group.pristine && this.group.controls.times.length >= 2;

		this.group.markAllAsTouched();
		const currentTarget = ev.currentTarget as HTMLElement;
		// Defer until after app-time-input commits its blur value to the parent form control.
		setTimeout(() => {
			if (currentTarget.contains(document.activeElement)) return;

			if (field === "end" && currentIndex !== undefined && currentIndex >= 0) {
				const currentControl = this.group.controls.times.controls[currentIndex];
				if (currentControl) {
					this.clampEntryToInitialBounds(currentControl, "end");
				}
			}

			if (field === "end" && currentIndex !== undefined && currentIndex >= 0 && canSyncAdjacent) {
				const timesControls = this.group.controls.times.controls;
				const nextIndex = currentIndex + 1;

				if (nextIndex < timesControls.length) {
					const currentControl = timesControls[currentIndex];
					const nextControl = timesControls[nextIndex];
					if (currentControl && nextControl) {
						const currentEndTime = currentControl.controls.end.value;
						if (currentEndTime) {
							nextControl.controls.start.patchValue(currentEndTime, { emitEvent: false });
							nextControl.updateValueAndValidity();
							currentControl.updateValueAndValidity();
						}
					}
				}
			}
		}, 0);
	}
	private sortTimes(force: boolean = false) {
		if (!force && (this.group.pristine || this.group.controls.times.length < 2)) return;
		const { times } = this.group.getRawValue();
		const sortBy = firstBy((d: any) => coerceTimeFormat(d.start).getTime(), "asc").thenBy(
			(d: any) => coerceTimeFormat(d.end ?? d.start).getTime(),
			"asc"
		);
		// this.group.patchValue({
		//   times: times.sort(sortBy),
		// });
		// also sort control instances - so static options are maintained to their origin value
		this.group.controls.times.controls = this.group.controls.times.controls.toSorted((a, b) =>
			sortBy(a.getRawValue(), b.getRawValue())
		);
		// todo - sorted clone to controls to kind of fix focus issue on re-sort
		// this.group.controls.times.controls = this.group.controls.times.controls.toSorted(
		//   firstBy((d: any) => coerceTimeFormat(d.value.start).getTime(), 'asc').thenBy(
		//     (d) => coerceTimeFormat(d.value.end ?? d.value.start).getTime(),
		//     'asc',
		//   ),
		// );
		log.debug({ sortedTimes: times });
		this.group.updateValueAndValidity();
		this.group.controls.times.updateValueAndValidity();
		if (this.isFullyRestrictedOnOwnTimes() || this.isCreateOnlyOnOwnTimes()) {
			this.applyLimitedUpdatePermissions();
		}
	}
	openUserPicker() {
		if (this.hasActiveRecording || !this.workspace$isAdmin.value) {
			return;
		}

		const user = this.group.value?.user;
		if (!user) return;
		this.dialog
			.open(UserSinglePickerDialogComponent, {
				data: <UserSinglePickerDialogData>{
					selected: this.group.value?.user,
					filter: ({ selected, ...x }) => {
						const status = ((user) => parseSubscriptionAsStatus(user.workspace, user))(
							this.userSettingsQuery.getValue()
						);
						return ((x.has_license || status.isTrial) && !x.removed) || selected;
					},
				},
			})
			.afterClosed()
			.subscribe((user) => {
				if (user) {
					this.patchValue({
						user,
					});
					this.group.controls.date.updateValueAndValidity();
				}
			});
	}
	calculateDuration(ctrl: TimesControl, ctrlIndex: number) {
		const value = ctrl.getRawValue();
		if (!value) return null;
		const start = coerceTimeFormat(value.start!).getTime(),
			end = coerceTimeFormat(value.end!).getTime();
		if (end < start) return null;
		return (end - start) / 1000;
	}
	get groupValue() {
		return this.group.getRawValue();
	}
	addTimeRange(
		type: ComegoTimeType,
		start: Date,
		end: Date,
		emitChangesOnAdd: boolean = true,
		recording: boolean = false,
		name: string,
		timeId?: string
	) {
		const isDisabledControl = (name: string) => !!this.data.disabledFields?.find((x) => x === name);
		const isExistingEntry = !!timeId;
		const newTimeGroup = new FormGroup<TimesSingleControl>({
			name: new FormControl<string>(name),
			id: new FormControl(timeId ?? null),
			start: new FormControl(format(start, "HH:mm")),
			end: new FormControl(format(end, "HH:mm")),
			type: new FormControl({ value: type, disabled: isDisabledControl("time.type") || recording }),
			recording: new FormControl(recording),
			user: new FormControl(null),
		});

		this.group.controls.times.push(newTimeGroup, { emitEvent: false });

		this.captureInitialBounds(newTimeGroup);

		// Only parse duration for new entries (not existing ones loaded from database)
		// For existing entries, preserve the saved end time from the database
		if (!isExistingEntry) {
			this.parseTimeDurationFromType(newTimeGroup);
		}

		// Set up change listeners for the newly added time group
		// These will trigger when user manually changes type or start time
		newTimeGroup.controls.start.valueChanges.subscribe(() => {
			this.parseTimeDurationFromType(newTimeGroup);
		});
		newTimeGroup.controls.type.valueChanges.subscribe(() => {
			this.parseTimeDurationFromType(newTimeGroup);
		});

		if (emitChangesOnAdd) this.group.controls.times.updateValueAndValidity();
	}
	addTime(type?: ComegoTimeType, duration?: number, reversi?: boolean) {
		const date = this.group.getRawValue()?.date || new Date();
		const timeValues = this.group.controls.times.getRawValue();
		const isDisabledControl = (name: string) => !!this.data.disabledFields?.find((x) => x === name);
		// Chronologically last form entry (by end time) for consistent suggestion
		const formEntriesWithEnd =
			timeValues?.map((t) => ({ endTs: coerceTimeFormat(t.end, date).getTime(), type: t.type })) ?? [];
		const lastFormByTime =
			formEntriesWithEnd.length > 0 ? formEntriesWithEnd.reduce((a, b) => (a.endTs >= b.endTs ? a : b)) : null;
		const wh = this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.["workinghours"];

		const suggestion = this.comegoSuggestion.getSuggestedNextEntry({
			date,
			existingEntries: this.getRetainedExistingEntries(),
			formEntries: timeValues?.map((t) => ({ start: t.start, end: t.end, type: t.type })) ?? [],
			lastFormEntry: lastFormByTime ? { endTs: lastFormByTime.endTs, type: lastFormByTime.type } : undefined,
			schedule: this.activeSchedule,
			workingHours: wh ?? undefined,
			newWorkEntryDurationMs: this.newWorkEntryDuration ?? undefined,
			preferredType: type ?? undefined,
		});
		const nextType = suggestion.type;
		const prevTime = suggestion.startTime;
		const nextTime = suggestion.endTime;

		if (this.data.allowedTimeTypes && !this.data.allowedTimeTypes?.find((x) => x === nextType)) return;
		if (reversi)
			this.group.controls.times.insert(
				0,
				new FormGroup<TimesSingleControl>({
					name: new FormControl<string>(null),
					id: new FormControl(null),
					end: new FormControl(format(nextTime, "HH:mm")),
					type: new FormControl({ value: nextType, disabled: isDisabledControl("time.type") }),
					start: new FormControl(format(prevTime, "HH:mm")),
					recording: new FormControl(false),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		else
			this.group.controls.times.push(
				new FormGroup<TimesSingleControl>({
					name: new FormControl<string>(null),
					id: new FormControl(null),
					end: new FormControl(format(nextTime, "HH:mm")),
					type: new FormControl({ value: nextType, disabled: isDisabledControl("time.type") }),
					start: new FormControl(format(prevTime, "HH:mm")),
					recording: new FormControl(false),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		this.group.controls.times.updateValueAndValidity();
	}
	deleteItem(index: number) {
		this.group.controls.times.removeAt(index);
		this.group.controls.times.controls.forEach((x) => x.updateValueAndValidity());
		this.group.controls.times.updateValueAndValidity();
		this.group.updateValueAndValidity();
		if (this.isFullyRestrictedOnOwnTimes()) {
			this.applyLimitedUpdatePermissions();
		}
	}

	isItemDeletable(ctrlIndex: number): boolean {
		const ctrl = this.group.controls.times.controls[ctrlIndex];
		if (!ctrl || ctrl.disabled) return false;
		if (this.data?.time?.lockItem && ctrlIndex === 0) return false;
		if (ctrl.controls.recording.value) {
			return this.isFullyRestrictedOnOwnTimes() && this.getLatestRecordingTimeControl() === ctrl;
		}
		if (this.isEditingOwnTimes() && this.isPreExistingEntry(ctrl) && !this.canUpdateTimes$.value) return false;
		const primaryId = this.getPrimaryRecordingEntry()?.id;
		const rowId = ctrl.controls.id.value;
		if (primaryId && rowId === primaryId && !this.canAddTime.value) return false;
		return true;
	}
	patchValue(data: Partial<typeof this.group.value> = {}, options?: Parameters<typeof this.group.patchValue>[1]) {
		const user = this.userSettingsQuery.getValue();
		if (data.user === null) data.user = user.workspace.users.find((x) => x.id === user.id);
		return this.group.patchValue(data, options);
	}

	private checkValueOnceChange(value: any) {
		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => stringify(x.times) === stringify(value)),
				tap((x) => log.debug("break rule check", x)),
				takeWhile((x) => x, true)
			)
			.subscribe((x) => !x && this.error$.next(null));
	}
	isFieldShown(name: string) {
		return !this.hiddenFields.value?.[name];
	}

	/** When the user cannot update times, only break-fix related rows stay editable. */
	isEntryFormDisabled(ctrl: TimesControl): boolean {
		if (!this.isEditingOwnTimes()) return false;
		if (this.canUpdateTimes$.value) return false;
		if (this.isFullyRestrictedOnOwnTimes() && this.getLatestClosedTimeControl() === ctrl) return false;
		if (this.isPreExistingEntry(ctrl)) return true;
		return !this.isBreakFixExemptEntry(ctrl);
	}

	private isEditingOwnTimes(): boolean {
		const formUserId = this.group.getRawValue()?.user?.id;
		const currentUserId = this.userSettingsQuery.getValue().id;
		if (!formUserId || formUserId === currentUserId) return true;
		const userSettings = this.userSettingsQuery.getValue();
		return !(isAdmin(userSettings) || isSupervisor(userSettings));
	}

	private isFullyRestrictedOnOwnTimes(): boolean {
		return this.isEditingOwnTimes() && !this.canUpdateTimes$.value && !this.canAddTime.value;
	}

	private isCreateOnlyOnOwnTimes(): boolean {
		return this.isEditingOwnTimes() && this.canAddTime.value && !this.canUpdateTimes$.value;
	}

	/** Chronologically last non-recording row with a start and end (by end time, tie-break by form order). */
	private getLatestClosedTimeControl(): TimesControl | null {
		const formDate = this.group.getRawValue()?.date;
		if (!formDate) return null;

		let latest: TimesControl | null = null;
		let latestEndTs = -1;
		let latestIndex = -1;

		this.group.controls.times.controls.forEach((ctrl, index) => {
			const raw = ctrl.getRawValue();
			if (raw.recording || !raw.start || !raw.end) return;
			const endTs = coerceTimeFormat(raw.end, formDate).getTime();
			if (!isValid(new Date(endTs))) return;
			if (endTs > latestEndTs || (endTs === latestEndTs && index > latestIndex)) {
				latestEndTs = endTs;
				latestIndex = index;
				latest = ctrl;
			}
		});

		return latest;
	}

	/** Chronologically last active recording row (by start time, tie-break by form order). */
	private getLatestRecordingTimeControl(): TimesControl | null {
		const formDate = this.group.getRawValue()?.date;
		if (!formDate) return null;

		let latest: TimesControl | null = null;
		let latestStartTs = -1;
		let latestIndex = -1;

		this.group.controls.times.controls.forEach((ctrl, index) => {
			const raw = ctrl.getRawValue();
			if (!raw.recording || !raw.start) return;
			const startTs = coerceTimeFormat(raw.start, formDate).getTime();
			if (!isValid(new Date(startTs))) return;
			if (startTs > latestStartTs || (startTs === latestStartTs && index > latestIndex)) {
				latestStartTs = startTs;
				latestIndex = index;
				latest = ctrl;
			}
		});

		return latest;
	}

	private buildEntryProvenanceMaps(): void {
		const serviceManagedIds = this.getServiceManagedIds();
		this.preExistingEntriesById = new Map(
			(this.data.time?.value ?? []).filter((e) => e.id && !serviceManagedIds.has(e.id)).map((e) => [e.id!, e])
		);
		for (const ctrl of this.group.controls.times.controls) {
			this.captureInitialBounds(ctrl);
		}
	}

	private captureInitialBounds(ctrl: TimesControl): void {
		const id = ctrl.controls.id.value;
		if (!id || this.initialBoundsById.has(id)) return;
		const raw = ctrl.getRawValue();
		if (!raw.start || !raw.end) return;
		this.initialBoundsById.set(id, {
			start: raw.start,
			end: raw.end,
			type: raw.type,
		});
	}

	private isPreExistingEntry(ctrl: TimesControl): boolean {
		const id = ctrl.controls.id.value;
		return !!id && this.preExistingEntriesById.has(id);
	}

	private getServiceManagedIds(): Set<string> {
		// Draft ids (prefix "draft:") are included so the dialog treats them as
		// service-managed rows for permission/clamping purposes.
		return new Set((this.wasRecordingItems.value ?? []).map((e) => e.id).filter(Boolean) as string[]);
	}

	private isServiceManagedRow(ctrl: TimesControl): boolean {
		const id = ctrl.controls.id.value;
		return !!id && this.getServiceManagedIds().has(id);
	}

	private getPrimaryRecordingEntry(): ComegoTime | null {
		return (this.wasRecordingItems.value ?? []).find((e) => e.type === "work") ?? null;
	}

	private isPrimaryRecordingRow(ctrl: TimesControl): boolean {
		const primaryId = this.getPrimaryRecordingEntry()?.id;
		return (
			ctrl.controls.type.value === "work" &&
			!!ctrl.controls.id.value &&
			!!primaryId &&
			ctrl.controls.id.value === primaryId
		);
	}

	private isWasRecordingRow(ctrl: TimesControl): boolean {
		return this.isServiceManagedRow(ctrl);
	}

	private isBreakFixPauseRow(ctrl: TimesControl): boolean {
		if (ctrl.controls.type.value !== "pause") return false;
		const date = this.group.getRawValue()?.date as Date | null;
		if (!date || !isValid(date)) return false;
		const pauseStart = parseTimeFormat(ctrl.controls.start.value, date);
		if (!isValid(pauseStart)) return false;
		const wasRecordingCtrl = this.group.controls.times.controls.find((c) => {
			if (c.controls.type.value !== "work" || !this.isServiceManagedRow(c) || !c.controls.end.value) return false;
			const workEnd = parseTimeFormat(c.controls.end.value, date);
			return isValid(workEnd) && isSameMinute(pauseStart, workEnd);
		});
		return !!wasRecordingCtrl;
	}

	private isNewBreakFixWorkRecordingRow(ctrl: TimesControl): boolean {
		const primaryId = this.getPrimaryRecordingEntry()?.id;
		return (
			ctrl.controls.type.value === "work" &&
			ctrl.controls.recording.value === true &&
			!!ctrl.controls.id.value &&
			this.isServiceManagedRow(ctrl) &&
			ctrl.controls.id.value !== primaryId
		);
	}

	private isClosedServiceFixMode(): boolean {
		if (!this.isEditingOwnTimes()) return false;
		if (this.canUpdateTimes$.value || this.canAddTime.value) return false;
		const items = this.wasRecordingItems.value ?? [];
		if (!items.length) return false;
		return items.every((e) => !!e.end);
	}

	private isRulesEnforced(): boolean {
		return !!this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours
			?.preventDeviatingWorkingHours;
	}

	private getOtherFormEntries(
		excludeCtrl: TimesControl
	): Array<{ start: string; end: string; type: ComegoTimeType }> {
		return this.group.controls.times.controls
			.filter((c) => c !== excludeCtrl)
			.map((c) => c.getRawValue())
			.filter((t) => t.start && t.end)
			.map((t) => ({ start: t.start!, end: t.end!, type: t.type }));
	}

	/** Latest allowed end for a work row given schedule target and daily max across all other entries. */
	private getMaxAllowedWorkEnd(ctrl: TimesControl, formDate: Date): Date | null {
		const raw = ctrl.getRawValue();
		if (raw.type !== "work" || raw.recording || !raw.start) return null;

		const start = coerceTimeFormat(raw.start, formDate);
		if (!isValid(start)) return null;

		const otherFormEntries = this.getOtherFormEntries(ctrl);
		const existingEntries = this.getRetainedExistingEntries();
		const wh = this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours;

		let maxEndTs = Infinity;

		const scheduleRemaining = this.comegoSuggestion.getRemainingTargetHoursForDay(
			this.activeSchedule,
			formDate,
			existingEntries,
			otherFormEntries
		);
		if (scheduleRemaining !== null) {
			maxEndTs = Math.min(maxEndTs, start.getTime() + scheduleRemaining);
		}

		const dailyRemaining = this.comegoSuggestion.getRemainingMaxHoursForDay(
			formDate,
			existingEntries,
			otherFormEntries,
			wh ?? undefined
		);
		if (dailyRemaining !== null) {
			maxEndTs = Math.min(maxEndTs, start.getTime() + dailyRemaining);
		}

		return maxEndTs === Infinity ? null : new Date(maxEndTs);
	}

	/** Clamps end to per-entry initial bound and combined schedule/daily-max limits. */
	private clampWorkEndToLimits(ctrl: TimesControl, formDate: Date, end: Date, initialEnd?: Date): Date {
		let clampedEnd = end;

		// Shortening never increases total work — only cap extensions toward schedule/daily max.
		const isShortening = !!initialEnd && end.getTime() < initialEnd.getTime();
		if (!isShortening) {
			const maxAllowedEnd = this.getMaxAllowedWorkEnd(ctrl, formDate);
			if (maxAllowedEnd && clampedEnd.getTime() > maxAllowedEnd.getTime()) {
				this.showContactAdmin.update(true);
				clampedEnd = maxAllowedEnd;
			}
		}
		const start = coerceTimeFormat(ctrl.getRawValue().start!, formDate);
		if (clampedEnd.getTime() <= start.getTime()) {
			clampedEnd = addMinutes(start, 1);
		}
		return clampedEnd;
	}

	private clampEntryToInitialBounds(editedCtrl?: TimesControl, _field?: "start" | "end"): void {
		if (!this.isEditingOwnTimes()) return;

		if (!this.canAddTime.value) {
			const formDate = this.group.getRawValue()?.date;
			if (!formDate) return;

			const clampCtrl = (ctrl: TimesControl) => {
				const id = ctrl.controls.id.value;
				if (!id || !this.isServiceManagedRow(ctrl)) return;
				const bounds = this.initialBoundsById.get(id);
				if (!bounds || bounds.type !== "work") return;
				const raw = ctrl.getRawValue();
				if (raw.type !== "work" || !raw.start || !raw.end) return;

				const start = coerceTimeFormat(raw.start, formDate);
				const end = coerceTimeFormat(raw.end, formDate);
				const initialStart = coerceTimeFormat(bounds.start, formDate);
				const initialEnd = coerceTimeFormat(bounds.end, formDate);
				if (!isValid(start) || !isValid(end) || !isValid(initialStart) || !isValid(initialEnd)) return;

				let changed = false;
				if (start.getTime() < initialStart.getTime()) {
					ctrl.controls.start.patchValue(bounds.start, { emitEvent: false });
					changed = true;
				}
				const clampedEnd = this.clampWorkEndToLimits(ctrl, formDate, end, initialEnd);
				if (clampedEnd.getTime() !== end.getTime()) {
					ctrl.controls.end.patchValue(format(clampedEnd, "HH:mm"), { emitEvent: false });
					changed = true;
				}
				if (changed) ctrl.updateValueAndValidity();
			};

			if (editedCtrl) {
				clampCtrl(editedCtrl);
			} else {
				for (const ctrl of this.group.controls.times.controls) {
					clampCtrl(ctrl);
				}
			}
		}

		if (this.isFullyRestrictedOnOwnTimes()) {
			this.clampLatestEndToInitialBounds(editedCtrl);
		}
	}

	private getInitialEndBound(ctrl: TimesControl): string | null {
		const id = ctrl.controls.id.value;
		if (id && this.initialBoundsById.has(id)) {
			return this.initialBoundsById.get(id)!.end;
		}
		if (id && this.preExistingEntriesById.has(id)) {
			const original = this.preExistingEntriesById.get(id)!;
			return original.end ? format(new Date(original.end), "HH:mm") : null;
		}
		return null;
	}

	/** When the user cannot edit or create times, latest end may only move earlier than at open. */
	private clampLatestEndToInitialBounds(editedCtrl?: TimesControl): void {
		if (!this.isFullyRestrictedOnOwnTimes()) return;

		const latest = this.getLatestClosedTimeControl();
		if (!latest) return;
		if (editedCtrl && editedCtrl !== latest) return;

		const formDate = this.group.getRawValue()?.date;
		if (!formDate) return;

		const raw = latest.getRawValue();
		if (!raw.start || !raw.end) return;

		const initialEndStr = this.getInitialEndBound(latest);
		if (!initialEndStr) return;

		const start = coerceTimeFormat(raw.start, formDate);
		const end = coerceTimeFormat(raw.end, formDate);
		const initialEnd = coerceTimeFormat(initialEndStr, formDate);
		if (!isValid(start) || !isValid(end) || !isValid(initialEnd)) return;

		const clampedEnd = this.clampWorkEndToLimits(latest, formDate, end, initialEnd);

		if (clampedEnd.getTime() !== end.getTime()) {
			latest.controls.end.patchValue(format(clampedEnd, "HH:mm"), { emitEvent: false });
			latest.updateValueAndValidity();
		}
	}

	private isBreakFixExemptEntry(ctrl: TimesControl): boolean {
		if (this.isEditingOwnTimes() && this.isPreExistingEntry(ctrl)) return false;
		if (this.isClosedServiceFixMode()) {
			return this.isServiceManagedRow(ctrl);
		}
		return (
			this.isWasRecordingRow(ctrl) ||
			this.isBreakFixPauseRow(ctrl) ||
			this.isNewBreakFixWorkRecordingRow(ctrl) ||
			ctrl.controls.recording.value === true
		);
	}

	private applyLimitedUpdatePermissions(): void {
		if (!this.canUpdateTimes$.value && this.isEditingOwnTimes()) {
			const hasServiceManaged = this.getServiceManagedIds().size > 0;
			const hasPreExisting = this.preExistingEntriesById.size > 0;
			if (hasServiceManaged || hasPreExisting) {
				if (hasServiceManaged) {
					if (this.isClosedServiceFixMode()) {
						for (const ctrl of this.group.controls.times.controls) {
							if (!this.isServiceManagedRow(ctrl)) {
								ctrl.disable({ emitEvent: false });
								continue;
							}
							ctrl.enable({ emitEvent: false });
							if (this.isPrimaryRecordingRow(ctrl)) {
								ctrl.controls.start.disable({ emitEvent: false });
								ctrl.controls.end.enable({ emitEvent: false });
								ctrl.controls.type.disable({ emitEvent: false });
								ctrl.controls.name.enable({ emitEvent: false });
							} else {
								ctrl.controls.start.enable({ emitEvent: false });
								ctrl.controls.end.enable({ emitEvent: false });
								ctrl.controls.type.disable({ emitEvent: false });
								ctrl.controls.name.enable({ emitEvent: false });
							}
						}
					} else {
						for (const ctrl of this.group.controls.times.controls) {
							if (ctrl.controls.recording.value === true) {
								ctrl.enable({ emitEvent: false });
								ctrl.controls.start.enable({ emitEvent: false });
								ctrl.controls.end.disable({ emitEvent: false });
								ctrl.controls.type.disable({ emitEvent: false });
								ctrl.controls.name.disable({ emitEvent: false });
								continue;
							}
							if (this.isWasRecordingRow(ctrl) && ctrl.controls.type.value === "work") {
								ctrl.enable({ emitEvent: false });
								ctrl.controls.start.disable({ emitEvent: false });
								ctrl.controls.end.enable({ emitEvent: false });
								ctrl.controls.type.disable({ emitEvent: false });
								ctrl.controls.name.disable({ emitEvent: false });
								continue;
							}
							if (
								this.isBreakFixPauseRow(ctrl) ||
								(this.isServiceManagedRow(ctrl) && ctrl.controls.type.value === "pause")
							) {
								ctrl.enable({ emitEvent: false });
								ctrl.controls.start.enable({ emitEvent: false });
								ctrl.controls.end.enable({ emitEvent: false });
								ctrl.controls.type.disable({ emitEvent: false });
								ctrl.controls.name.disable({ emitEvent: false });
								continue;
							}
							ctrl.disable({ emitEvent: false });
						}
					}
				}

				for (const ctrl of this.group.controls.times.controls) {
					if (this.isPreExistingEntry(ctrl)) {
						ctrl.disable({ emitEvent: false });
					}
				}

				if (this.isFullyRestrictedOnOwnTimes()) {
					const latest = this.getLatestClosedTimeControl();
					if (latest) {
						latest.enable({ emitEvent: false });
						if (this.isPrimaryRecordingRow(latest)) {
							latest.controls.start.disable({ emitEvent: false });
						} else {
							latest.controls.start.enable({ emitEvent: false });
						}
						latest.controls.type.disable({ emitEvent: false });
						latest.controls.name.disable({ emitEvent: false });
						latest.controls.end.enable({ emitEvent: false });
						this.captureInitialBounds(latest);
					}
				}

				if (this.isCreateOnlyOnOwnTimes()) {
					const latest = this.getLatestClosedTimeControl();
					if (latest) {
						latest.enable({ emitEvent: false });
						latest.controls.start.enable({ emitEvent: false });
						latest.controls.end.enable({ emitEvent: false });
					}
				}
			}
		}
		this.updateSoleEditableTimeFieldHighlight();
	}

	private collectEditableTimeFields(): Array<{ rowIndex: number; field: "start" | "end" }> {
		const editable: Array<{ rowIndex: number; field: "start" | "end" }> = [];
		this.group.controls.times.controls.forEach((ctrl, rowIndex) => {
			if (ctrl.disabled) return;
			if (ctrl.controls.start.enabled) editable.push({ rowIndex, field: "start" });
			if (ctrl.controls.end.enabled) editable.push({ rowIndex, field: "end" });
		});
		return editable;
	}

	private updateSoleEditableTimeFieldHighlight(): void {
		const editable = this.collectEditableTimeFields();
		this.soleEditableTimeField = editable.length === 1 ? editable[0] : null;
		this.scheduleFocusSoleEditableTimeField();
	}

	private scheduleFocusSoleEditableTimeField(): void {
		if (!this.soleEditableTimeField || this.hasSelectedSoleEditableField || this.isLoading.value) return;
		if (this.soleEditableFocusTimer) clearTimeout(this.soleEditableFocusTimer);
		this.soleEditableFocusTimer = setTimeout(() => {
			this.soleEditableFocusTimer = null;
			if (!this.soleEditableTimeField || this.hasSelectedSoleEditableField || this.isLoading.value) return;
			requestAnimationFrame(() => this.focusSoleEditableTimeField());
		}, 250);
	}

	private getSoleEditableTimeInput(): HTMLInputElement | null {
		const target = this.soleEditableTimeField;
		if (!target) return null;

		const rows = this.hostEl.nativeElement.querySelectorAll(".time-inputs-row");
		const row = rows.item(target.rowIndex) as HTMLElement | null;
		if (!row) return null;

		const inputIndex = target.field === "start" ? 0 : 1;
		const input = row.querySelectorAll("input").item(inputIndex) as HTMLInputElement | null;
		if (!input || input.disabled) return null;
		return input;
	}

	private focusSoleEditableTimeField(): void {
		if (!this.soleEditableTimeField || this.hasSelectedSoleEditableField) return;

		const input = this.getSoleEditableTimeInput();
		if (!input) return;

		input.focus({ preventScroll: true });
		input.select();

		// Mat dialog focus trap / late permission updates can steal focus briefly after open.
		setTimeout(() => {
			if (!this.soleEditableTimeField || this.hasSelectedSoleEditableField) return;
			const retryInput = this.getSoleEditableTimeInput();
			if (!retryInput) return;
			retryInput.focus({ preventScroll: true });
			retryInput.select();
			this.hasSelectedSoleEditableField = true;
		}, 100);
	}

	isSoleEditableTimeField(rowIndex: number, field: "start" | "end"): boolean {
		return this.soleEditableTimeField?.rowIndex === rowIndex && this.soleEditableTimeField?.field === field;
	}

	private enforceSubmitPermissions(): boolean {
		if (!this.isEditingOwnTimes()) return true;

		if (!this.canAddTime.value) {
			this.clampEntryToInitialBounds();
		}

		if (!this.canUpdateTimes$.value) {
			const formTimes = this.group.getRawValue().times ?? [];
			const formIds = new Set(formTimes.map((t) => t.id).filter(Boolean));

			for (const [id, original] of this.preExistingEntriesById) {
				if (!formIds.has(id)) {
					const start = new Date(original.start);
					const end = original.end ? new Date(original.end) : start;
					const type = (original.type ?? "work") as ComegoTimeType;
					this.addTimeRange(type, start, end, false, !original.end, original.name ?? null, id);
				}
			}

			for (const ctrl of this.group.controls.times.controls) {
				if (!this.isPreExistingEntry(ctrl)) continue;
				const id = ctrl.controls.id.value;
				const original = id ? this.preExistingEntriesById.get(id) : undefined;
				if (!original) continue;
				const origStart = format(new Date(original.start), "HH:mm");
				const origEnd = original.end ? format(new Date(original.end), "HH:mm") : null;
				const raw = ctrl.getRawValue();
				const type = (original.type ?? raw.type) as ComegoTimeType;
				const isLatestRestrictedEndEdit =
					this.isFullyRestrictedOnOwnTimes() && this.getLatestClosedTimeControl() === ctrl;

				if (isLatestRestrictedEndEdit) {
					if (raw.start !== origStart || raw.type !== type) {
						ctrl.patchValue(
							{
								start: origStart,
								type,
								name: original.name ?? raw.name,
							},
							{ emitEvent: false }
						);
					}
					this.clampLatestEndToInitialBounds(ctrl);
					continue;
				}

				if (raw.start !== origStart || raw.end !== origEnd || raw.type !== type) {
					ctrl.patchValue(
						{
							start: origStart,
							end: origEnd,
							type,
							name: original.name ?? raw.name,
						},
						{ emitEvent: false }
					);
				}
			}

			this.sortTimes(true);
			this.applyLimitedUpdatePermissions();
		}

		return true;
	}

	get hasActiveRecording(): boolean {
		return this.group.controls.times.controls.some((timeControl) => timeControl.controls.recording.value === true);
	}
	get pendingRecordingFix(): boolean {
		return !!this.data.pendingRecordingFix;
	}
	get pendingProjectionFix(): boolean {
		return !!this.data.pendingProjectionFix;
	}
	get violationNotices(): Array<{
		message: string;
		args?: Record<string, unknown>;
		start?: string;
		end?: string;
	}> {
		return this.data.violationNotices ?? [];
	}
	/** Close without writing form data; caller persists the original violating projection. */
	saveWithViolation(): void {
		this.ref.close(PROCEED_WITH_VIOLATION);
	}
	async deleteTimes(ids: string[]) {
		if (!ids?.length) return;
		await this.comegoService.delete(...(ids.map((id) => ({ id })) as any));
	}
	private isRecreateDayFlow(): boolean {
		return !!(this.data.forceRecreateDay || this.data.pendingProjectionFix || this.data.pendingRecordingFix);
	}
	/** Persisted ids that recreate/projection/recording submit will delete before re-adding. */
	private getIdsBeingReplaced(): Set<string> {
		if (this.isRecreateDayFlow()) {
			return new Set(this.getPersistedDayEntryIds());
		}
		const ids = new Set<string>();
		for (const id of this.data.idsToDelete ?? []) {
			if (id && !isDraftId(id)) ids.add(id);
		}
		return ids;
	}
	/**
	 * Store entries that should still count for overlap/duration/max checks.
	 * Excludes rows listed for delete on recreate and ids already present on form rows.
	 */
	private getRetainedExistingEntries(existingEntries?: ComegoTime[] | null): ComegoTime[] {
		const replaceIds = this.getIdsBeingReplaced();
		const formIds = new Set<string>();
		for (const ctrl of this.group.controls.times.controls) {
			const id = ctrl.value?.id;
			if (id && !isDraftId(id)) formIds.add(id);
		}
		return (existingEntries ?? this.existingEntries$.value ?? []).filter(
			(entry) => !!entry.id && !replaceIds.has(entry.id) && !formIds.has(entry.id)
		);
	}
	/** Loads same-day store entries when the dialog will recreate/replace the day on submit. */
	private loadExistingEntriesForRecreateFlow(): void {
		if (!this.isRecreateDayFlow()) return;
		const userId = this.group.getRawValue()?.user?.id;
		const date = this.group.getRawValue()?.date;
		if (!userId || !date || !isValid(date)) {
			this.existingEntries$.next([]);
			return;
		}
		const raw = this.comegoQuery.getAll({
			filterBy: (entry) => !!entry.start && entry.user?.id === userId && isSameDay(new Date(entry.start), date),
			sortBy: (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
		});
		this.existingEntries$.next([...raw].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()));
	}
	/** Collects persisted ids for the loaded day (excludes client-side drafts). */
	private getPersistedDayEntryIds(): string[] {
		const ids = new Set<string>();
		const addId = (id?: string | null) => {
			if (id && !isDraftId(id)) ids.add(id);
		};
		for (const entry of this.data.time?.value ?? []) {
			addId(entry.id);
		}
		for (const entry of this.wasRecordingItems.value ?? []) {
			addId(entry.id);
		}
		for (const ctrl of this.group.controls.times.controls) {
			addId(ctrl.value?.id);
		}
		for (const id of this.data.idsToDelete ?? []) {
			addId(id);
		}
		// Store rows for recreate flows — required so suggestion submit deletes the
		// current day even when the form only has id-less suggested drafts.
		for (const entry of this.existingEntries$.value ?? []) {
			addId(entry.id);
		}
		return [...ids];
	}
	async submit() {
		if (!this.enforceSubmitPermissions()) return;
		this.clampEntryToInitialBounds();
		let { times, user, date: now } = this.group.getRawValue();
		if (!times.length) return;
		const validTypes: ComegoTimeType[] = ["work", "pause", "absence"];
		times = times.map((x) => {
			if (!validTypes.includes(x.type)) {
				x.typeId = x.type;
				x.type = "absence";
			}
			return x;
		});
		this.isLoading.update(true);

		// Deferred-persist path: delete all persisted entries for the day, then add form rows.
		if (this.data.pendingRecordingFix || this.data.pendingProjectionFix) {
			const idsToDelete = this.getPersistedDayEntryIds();
			let error: any;
			if (idsToDelete.length) {
				await this.deleteTimes(idsToDelete).catch((err) => {
					error = err;
					this.isLoading.update(false);
					pushError(err);
				});
			}
			if (error) return;
			const timesToAdd = times.map((r) => {
				const start = resolveEntryTime(r.start, now);
				const end = r.recording ? null : resolveEntryTime(r.end, now);
				return omit({ ...r, start, end, user }, "id") as any;
			});
			if (timesToAdd.length > 0) {
				await this.comegoService
					.add(...timesToAdd)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
					.catch(async (err: HttpErrorResponse) => {
						this.isLoading.update(false);
						if (isWorkingHoursError(err.error)) {
							this.error$.next(parseErrorResponse(err));
							this.checkValueOnceChange(times);
						} else {
							pushError(err);
						}
						return Promise.reject(err);
					})
					.then((created) => {
						this.app.notifySuccess("success.updated-plural");
						this.ref.close(created);
					});
			} else {
				this.app.notifySuccess("success.updated-plural");
				this.ref.close([]);
			}
			this.isLoading.update(false);
			return;
		}

		const createTimes = times.reduce((acc, r) => {
			const start = coerceTimeFormat(r.start, now),
				end = coerceTimeFormat(r.end, now);
			acc.push({ ...r, start, end, user });
			return acc;
		}, []);
		const recordingsToRecreate = times
			.filter((d) => d.recording && d.id)
			.reduce<{ oldId: string; entry: any }[]>((acc, r) => {
				const original = this.data.time.value?.find((o) => o.id === r.id);
				if (!original) return acc;
				const originalStartHHMM = format(new Date(original.start), "HH:mm");
				const startChanged = r.start !== originalStartHHMM;
				const typeChanged = r.type !== original.type;
				if (!startChanged && !typeChanged) return acc;
				acc.push({
					oldId: r.id,
					entry: {
						name: r.name ?? null,
						type: r.type,
						start: coerceTimeFormat(r.start, now),
						end: null,
						user,
					},
				});
				return acc;
			}, []);
		let error: any;
		const shouldRecreateDay =
			this.data.forceRecreateDay || times.find((d) => !d.id) || recordingsToRecreate.length > 0;
		const existingTimes = times.filter((d) => d.id);
		const timesToDelete: { id: string }[] =
			(this.data.time.value?.length && xorBy(existingTimes, this.data.time.value as any, (d) => d.id)) || [];
		if (shouldRecreateDay) {
			const idsToDelete = [
				...new Set([...this.getPersistedDayEntryIds(), ...recordingsToRecreate.map((r) => r.oldId)]),
			];
			if (idsToDelete.length) {
				await this.deleteTimes(idsToDelete).catch((err) => {
					error = err;
					this.isLoading.update(false);
					pushError(err);
				});
			}
		} else if (timesToDelete.length) {
			await this.comegoService.delete(...(timesToDelete.map(({ id }) => ({ id })) as any));
		}
		if (error) return;
		if (shouldRecreateDay) {
			const timesToAdd = [
				...createTimes.filter((d) => !d.recording).map((d) => omit(d, "id") as any),
				...recordingsToRecreate.map((r) => r.entry),
			];
			if (timesToAdd.length > 0) {
				await this.comegoService
					.add(...timesToAdd)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
					.catch(async (err: HttpErrorResponse) => {
						this.isLoading.update(false);
						if (isWorkingHoursError(err.error)) {
							this.error$.next(parseErrorResponse(err));
							this.checkValueOnceChange(times);
						} else {
							pushError(err);
						}
						return Promise.reject(err);
					})
					.then((times) => {
						this.app.notifySuccess("success.updated-plural");
						this.ref.close(times);
					});
			} else {
				this.app.notifySuccess("success.updated-plural");
				this.ref.close([]);
			}
		} else {
			const timesToUpdate = createTimes.filter((d) => !d.recording);
			if (timesToUpdate.length > 0) {
				await this.comegoService
					.update(...timesToUpdate)
					.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
					.catch(async (err: HttpErrorResponse) => {
						this.isLoading.update(false);
						if (isWorkingHoursError(err.error)) {
							this.error$.next(parseErrorResponse(err));
							this.checkValueOnceChange(times);
						} else {
							pushError(err);
						}
						return Promise.reject(err);
					})
					.then((times) => {
						this.app.notifySuccess("success.updated-plural");
						this.ref.close(times);
					});
			} else {
				this.app.notifySuccess("success.updated-plural");
				this.ref.close([]);
			}
		}
		this.isLoading.update(false);
	}

	allowedDates() {
		const pastDate = getComegoLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		const futureDate = getComegoFutureLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		return [pastDate, futureDate];
	}

	openCalPicker() {
		const hasActiveRecording = this.group.controls.times.controls.some(
			(timeControl) => timeControl.controls.recording.value === true
		);
		if (hasActiveRecording) {
			return;
		}

		const dates = this.allowedDates();
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(this.group.getRawValue().date),

					minDate: dates[0],
					maxDate: dates[1],
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.patchValue({
					date: x,
				});
			});
	}
	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}
	calcType(type: string) {
		switch (type) {
			case "work_history":
			case "pause":
			case "home_filled":
				return type;
			default:
				return "beach_access";
		}
	}
	async changeDescriptionsForToday(currentDescription: string, applyToAllDialog?: TemplateRef<any>) {
		const timeControls: FormArray<TimesControl> = this.group.controls.times;
		//get value if this is the 2nd change
		const edited = this.userHasChangedDescription.value;
		if (!currentDescription || edited || timeControls.controls.length < 2) return;
		this.userHasChangedDescription.next(true);
		//confirm replaceAll dialog
		const replaceAll = await toPromise(
			this.dialog
				.open(applyToAllDialog, {
					data: {
						entity: null,
					},
				})
				.afterClosed()
		);
		if (replaceAll) {
			for (let formGroup of timeControls.controls) {
				if (formGroup.controls.type.value === "pause") continue;
				formGroup.controls.name.setValue(currentDescription);
			}
		}
	}

	parseTimeDurationFromType(formGroup: TimesControl) {
		const userSettings = this.userSettingsQuery.getValue();
		const absenceType = this.absenceSubcategories.find((absence) => absence.id === formGroup.controls.type.value);
		const userId = this.group.controls.user.value.id;
		if (!absenceType) {
			this.keepLatestEndDisabledIfRequired(formGroup);
			return;
		}
		formGroup.controls.end.enable();
		if (absenceType.isStatic || absenceType.durationType == "basedOnTargetHours") {
			if (!isAdmin(userSettings) && !isSubordinate(userSettings, userId)) formGroup.controls.end.disable();
		}
		const date = this.group.controls.date.value;
		let duration = "";
		if (absenceType.durationType == "basedOnTargetHours") {
			formGroup.controls.end.enable();
			duration = this.getScheduleForUser(userId, date);
		} else {
			duration = absenceType.duration;
		}
		const endString = this.sumTimes([formGroup.controls.start.value, duration]);
		formGroup.controls.end.patchValue(endString);
		this.sortTimes();
		this.keepLatestEndDisabledIfRequired(formGroup);
	}

	/** Re-apply latest-row end lock after absence duration parsing may have enabled it. */
	private keepLatestEndDisabledIfRequired(ctrl: TimesControl): void {
		if (!this.isEditingOwnTimes()) return;
		if (this.isFullyRestrictedOnOwnTimes()) return;
		if (!this.isCreateOnlyOnOwnTimes()) return;
		if (this.getLatestClosedTimeControl() !== ctrl) return;
		ctrl.controls.end.disable({ emitEvent: false });
	}

	getScheduleForUser(userId: string, date: Date) {
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: userId }, date);

		if (!times || times.length == 0) return "00:00";

		let hours: string[] = [];
		times
			.filter((time) => {
				return time.type == "work";
			})
			.forEach((time) => {
				hours.push(this.calculateScheduleDuration(time));
			});

		return this.sumTimes(hours);
	}
	sumTimes(times: string[]): string {
		let totalMinutes = 0;

		times.forEach((time) => {
			if (!time) return;
			const parts = time.split(":");
			if (parts.length < 2) return;
			const [hours, minutes] = parts.map(Number);
			if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
			totalMinutes += hours * 60 + minutes;
		});

		const sumHours = Math.floor(totalMinutes / 60);
		const sumMinutes = totalMinutes % 60;

		return `${String(sumHours).padStart(2, "0")}:${String(sumMinutes).padStart(2, "0")}`;
	}
	calculateScheduleDuration(entry: ComeGoTime): string {
		if (!entry?.start || !entry?.end) return "00:00";
		const [startHour, startMinute] = entry.start.split(":").map(Number);
		const [endHour, endMinute] = entry.end.split(":").map(Number);
		if ([startHour, startMinute, endHour, endMinute].some((n) => Number.isNaN(n))) return "00:00";

		const startTotal = startHour * 60 + startMinute;
		const endTotal = endHour * 60 + endMinute;

		const diffMinutes = endTotal - startTotal;

		const hours = Math.floor(diffMinutes / 60);
		const minutes = diffMinutes % 60;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	}

	readonly absencesForUser = useAbsencesForUser();

	formatTimeForDisplay(dateString: string | Date): string {
		const date = typeof dateString === "string" ? new Date(dateString) : dateString;
		return format(date, "HH:mm");
	}

	calculateExistingEntryDuration(entry: ComegoTime): number {
		if (!entry.start || !entry.end) return 0;
		const start = typeof entry.start === "string" ? new Date(entry.start) : entry.start;
		const end = typeof entry.end === "string" ? new Date(entry.end) : entry.end;
		return (end.getTime() - start.getTime()) / 1000; // return in seconds for parseMsAsDuration pipe
	}

	trackByEntryId(index: number, entry: ComegoTime): string {
		return entry.id || `${entry.start}-${entry.end}-${index}`;
	}

	private resolveOpenRecordingForNormalStop(): ComegoTime | null {
		const fromData = this.data.openRecording;
		if (fromData?.id && fromData.start) {
			const user = fromData.user ?? this.group.getRawValue().user;
			return { ...fromData, end: null, type: "work", user } as ComegoTime;
		}
		const persisted = (this.wasRecordingItems.value ?? []).find(
			(e) =>
				e.id &&
				!isDraftId(e.id) &&
				e.start &&
				(e.type === "work" || (e.type as { name?: string })?.name === "work")
		);
		if (!persisted) return null;
		const user = persisted.user ?? this.group.getRawValue().user;
		return { ...persisted, end: null, type: "work", user } as ComegoTime;
	}

	async discardRecording(confirmDeleteTemplate?: TemplateRef<any>) {
		if (this.data.pendingRecordingFix) {
			const preventDeviating =
				!!this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours
					?.preventDeviatingWorkingHours;
			if (!preventDeviating) {
				const openRecording = this.resolveOpenRecordingForNormalStop();
				if (!openRecording?.id) {
					this.ref.close(PROCEED_WITH_NORMAL_STOP);
					return;
				}
				this.isLoading.next(true);
				this.group.disable();
				try {
					const stopped = await this.recordService.performNormalComegoStop(openRecording);
					this.recordService.notifySuccess("success.created");
					this.ref.close(stopped?.length ? stopped : PROCEED_WITH_NORMAL_STOP);
				} catch {
					this.isLoading.next(false);
					this.group.enable();
				}
			}
			return;
		}

		this.isLoading.next(true);
		this.group.disable();
		if (confirmDeleteTemplate) {
			const allowDelete = await this.dialog
				.open(confirmDeleteTemplate, {
					data: {
						entity: this.wasRecordingItems.value?.[0],
					},
				})
				.afterClosed()
				.toPromise()
				.then((x) => x === true)
				.catch(() => false);
			if (!allowDelete) {
				this.isLoading.next(false);
				this.group.enable();
				return;
			}
		}
		const ids = (this.wasRecordingItems.value ?? []).map((e) => e.id).filter(Boolean) as string[];
		if (!ids.length) {
			this.isLoading.next(false);
			this.group.enable();
			return;
		}
		await this.deleteTimes(ids);
		this.recordService.notifySuccess("success.recordingDiscarded");
		this.ref.close(null);
	}

	async fixWorkinghourError(error: { content: string; args: any }) {
		const workspaceRules = this.userSettingsQuery.getValue().workspace.schedules.workspace[
			"workinghours"
		] as WorkingHourSettings;
		const minBreakDurationForCreditMS = (workspaceRules.minimumBreakDurationForCredit ?? 0.25) * 3.6e6;
		const date = this.group.value.date;
		const times = this.group.controls.times.controls
			.map((ctrl, index) => ({
				ctrl,
				index,
				start: parseTimeFormat(ctrl.value.start, date),
				end: parseTimeFormat(ctrl.value.end, date),
				durationMS:
					parseTimeFormat(ctrl.value.end, date).getTime() - parseTimeFormat(ctrl.value.start, date).getTime(),
			}))
			.sort((a, b) => a.start.getTime() - b.start.getTime());
		const totalWorkMs = times
			.filter((t) => t.ctrl.value.type === "work")
			.reduce((sum, t) => {
				if (!t.start || !t.end) return sum;
				return sum + (t.end.getTime() - t.start.getTime());
			}, 0);
		const wasRecording = this.getPrimaryRecordingEntry();
		//
		if (error.content === "errors.times.comego.breakRuleNotice") {
			const breakDuration = timeFormatToDuration(error.args.minutesDue) * 1000;
			const workingHoursMs = timeFormatToDuration(error.args.workingHours) * 1000;
			// find breaks that are valid (longer them workspaceRules.minimumBreakDurationForCredit)
			const totalBreak = times
				.filter((t) => t.ctrl.value.type === "pause")
				.reduce((sum, t) => {
					if (t.durationMS >= minBreakDurationForCreditMS) return sum + t.durationMS;
					return sum;
				}, 0);
			const neededBreakDuration = clamp(breakDuration - totalBreak, minBreakDurationForCreditMS, breakDuration);
			let gapStart: Date | null = null;

			for (let i = 0; i < times.length - 1; i++) {
				const currentEnd = times[i].end;
				const nextStart = times[i + 1].start;

				const gapMs = nextStart.getTime() - currentEnd.getTime();

				if (gapMs >= neededBreakDuration) {
					gapStart = currentEnd;
					break;
				}
			}
			if (gapStart) {
				const breakStart = gapStart;
				const breakEnd = addMilliseconds(breakStart, neededBreakDuration);
				this.addTimeRange("pause", breakStart, breakEnd, false, false, "");
			} else {
				let splitInserted = false;
				let continuousWorkMs = 0;

				for (const entry of times) {
					if (entry.ctrl.value.type === "work") {
						const prevContinuous = continuousWorkMs;
						continuousWorkMs += entry.durationMS;

						if (prevContinuous < workingHoursMs && continuousWorkMs > workingHoursMs) {
							const msIntoEntry = workingHoursMs - prevContinuous;
							const splitPoint = addMilliseconds(entry.start, msIntoEntry);
							const breakEnd = addMilliseconds(splitPoint, neededBreakDuration);
							const originalEnd = entry.end;

							entry.ctrl.patchValue({ end: format(splitPoint, "HH:mm") });
							this.addTimeRange("pause", splitPoint, breakEnd, false, false, "");

							if (breakEnd.getTime() < originalEnd.getTime()) {
								const name = entry.ctrl.value.name ?? "";
								this.addTimeRange("work", breakEnd, originalEnd, false, false, name);
							}
							splitInserted = true;
							break;
						}
					} else if (entry.ctrl.value.type === "pause" && entry.durationMS >= minBreakDurationForCreditMS) {
						continuousWorkMs = 0;
					}
				}

				if (!splitInserted) {
					// else add break before recording
					const target = this.group.controls.times.controls.find(
						//@ts-ignore
						(ctrl) => ctrl.value.id === wasRecording.id
					);

					if (target) {
						const start = new Date(wasRecording.start);
						const end = addMilliseconds(start, breakDuration);
						this.addTimeRange("pause", start, end, false, false, "");

						const newStart = addMilliseconds(start, breakDuration);
						const originalEnd = parseTimeFormat(target.value.end, start);
						const newEnd = addMilliseconds(originalEnd, breakDuration);

						target.patchValue({
							start: format(newStart, "HH:mm"),
							end: format(newEnd, "HH:mm"),
						});
					}
				}
			}
		}
		if (error.content === "errors.times.comego.breakBetweenWorkingDays") {
			// to next day
			// reduce las work entry end time
			const requiredRestMs = workspaceRules.breakBetweenWorkingDays * 3600 * 1000;
			const yesterday = addDays(date, -1);
			const nextDay = addDays(date, 1);
			const prevDayWork = this.comegoQuery.getAll({
				filterBy: (x) =>
					isSameDay(new Date(x.start), yesterday) &&
					x.user?.id === this.group.value.user?.id &&
					x.type === "work",
				sortBy(a, b) {
					const aend = new Date(a.end || new Date()),
						bend = new Date(b.end || new Date());
					return bend.getTime() - aend.getTime();
				},
			})?.[0];
			const nextDayWork = this.comegoQuery.getAll({
				filterBy: (x) =>
					isSameDay(new Date(x.start), nextDay) &&
					x.user?.id === this.group.value.user?.id &&
					x.type === "work",
				sortBy(a, b) {
					const astart = new Date(a.start || new Date()),
						btime = new Date(b.start || new Date());
					return astart.getTime() - btime.getTime();
				},
			})?.[0];
			const first = times[0];
			const last = times[times.length - 1];
			if (nextDayWork && new Date(nextDayWork.start).getTime() - new Date(last.end).getTime() < requiredRestMs) {
				const differenceMs =
					new Date(nextDayWork.start).getTime() - new Date(last.end).getTime() - requiredRestMs;
				const newEnd = addMilliseconds(last.end, differenceMs);
				if (newEnd.getTime() > last.start.getTime()) last.ctrl.patchValue({ end: format(newEnd, "HH:mm") });
				// if yes change the end of todays last item so the difference between the end of the last item and the start of the next day work is the required rest time
				// last item of the day has to be longer then 1 minute
			}
			if (prevDayWork && new Date(first.start).getTime() - new Date(prevDayWork.end).getTime() < requiredRestMs) {
				const differenceMs =
					requiredRestMs - (new Date(first.start).getTime() - new Date(prevDayWork.end).getTime());
				const newStart = addMilliseconds(first.start, differenceMs);
				if (newStart.getTime() < first.end.getTime())
					first.ctrl.patchValue({ start: format(newStart, "HH:mm") });
				// if yes change the start of todays first item so the difference between the end of the previous day and the start of the first item is the required rest time
				// first item of the day has to be longer then 1 minute
			}
		}
		if (error.content === "errors.times.comego.dailyMaxHours") {
			const maxAllowedTime = timeFormatToDuration(error.args.dailyMaxHours) * 1000;
			let differenceMs = totalWorkMs - maxAllowedTime;
			const maxIterations = this.group.controls.times.controls.length + 5;
			let iterations = 0;
			while (differenceMs > 0 && iterations < maxIterations) {
				iterations++;
				const workRows = times.filter(
					(row) =>
						row.ctrl.value.type === "work" &&
						row.start &&
						row.end &&
						row.end.getTime() > row.start.getTime()
				);
				if (!workRows.length) break;
				let latest = workRows[0];
				for (const row of workRows) {
					if (
						row.end.getTime() > latest.end.getTime() ||
						(row.end.getTime() === latest.end.getTime() && row.index > latest.index)
					) {
						latest = row;
					}
				}
				const segmentMs = latest.end.getTime() - latest.start.getTime();
				if (segmentMs <= differenceMs) {
					this.deleteItem(latest.index);
					differenceMs -= segmentMs;
				} else {
					const newEnd = addMilliseconds(latest.end, -differenceMs);
					latest.ctrl.patchValue({ end: format(newEnd, "HH:mm") });
					differenceMs = 0;
				}
			}
		}

		// sort Times and check Validity
		this.group.controls.times.updateValueAndValidity();
		this.sortTimes(true);
	}

	cancelAction() {
		this.ref.close(null);
	}
}
